import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AccessibleLoader } from '@/components/shared/AccessibleLoader';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { NewAuditForm, type NewAuditRequest } from '@/components/site-audit/NewAuditForm';
import { useGenerationPolling } from '@/hooks/useGenerationPolling';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { builderApi, projectsApi, siteAuditApi, type Build, type SiteAudit } from '@/utils/api-pb';
import type { Project } from '@/utils/pocketbase';

/** Colour bands match CategoryScoreBar: same question, same three answers. */
function scoreTone(score: number): string {
  if (score >= 80) return 'text-[#2e7d32]';
  if (score >= 50) return 'text-[#a35b00]';
  return 'text-[color:var(--error-strong,#b3261e)]';
}

/**
 * Audits the user has run, newest first, and the form that starts another.
 *
 * The list is fetched without the `report` field — a report is up to a megabyte and
 * this page shows a score and a date. The heavy part is loaded by the report page, for
 * the one audit being read.
 */
export default function SiteAuditListPage() {
  const { t, i18n } = useTranslation('siteAudit');
  const navigate = useLocalizedNavigate();

  const [audits, setAudits] = useState<SiteAudit[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [builds, setBuilds] = useState<Build[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SiteAudit | null>(null);

  const { startPolling, stopPolling } = useGenerationPolling({
    collection: 'site_audits',
    intervalMs: 3000,
    // Пайплайн без браузера укладывается в 5–15 секунд; reaper на бэкенде закрывает
    // зависшие записи через 15 минут, дольше ждать здесь нет смысла.
    maxDurationMs: 3 * 60 * 1000,
    onCompleted: (record) => {
      setPendingId(null);
      void load();
      navigate(`/site-audit/${record.id}`);
    },
    onFailed: (record) => {
      setPendingId(null);
      toast.error(record.error || t('toast.failed'));
      void load();
    },
    onTimeout: () => {
      setPendingId(null);
      toast.error(t('toast.timeout'));
      void load();
    },
  });

  useEffect(() => {
    void load();
    return () => stopPolling();
  }, [stopPolling]);

  async function load() {
    setIsLoading(true);
    const [auditsResult, projectsResult, buildsResult] = await Promise.allSettled([
      siteAuditApi.list(),
      projectsApi.list(),
      builderApi.list(),
    ]);

    if (auditsResult.status === 'fulfilled') setAudits(auditsResult.value);
    if (projectsResult.status === 'fulfilled') setProjects(projectsResult.value);
    // Аудит смотрит на живую страницу, поэтому черновики предлагать нечего.
    if (buildsResult.status === 'fulfilled') {
      setBuilds(buildsResult.value.builds.filter((build) => build.status === 'published'));
    }
    setIsLoading(false);
  }

  async function handleStart(request: NewAuditRequest) {
    setIsStarting(true);
    try {
      const audit = await siteAuditApi.start({ ...request, lang: i18n.language as 'ru' | 'en' });
      setPendingId(audit.id);
      startPolling(audit.id);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    } finally {
      setIsStarting(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);

    try {
      await siteAuditApi.delete(target.id);
      setAudits((current) => current.filter((audit) => audit.id !== target.id));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    }
  }

  return (
    <div className="flex flex-col gap-[24px]">
      <PageHeader title={t('page.title')} description={t('page.description')} />

      <NewAuditForm
        projects={projects}
        builds={builds}
        isSubmitting={isStarting || pendingId !== null}
        onSubmit={handleStart}
      />

      {pendingId && (
        <p className="rounded-[16px] bg-[rgba(33,151,241,0.08)] px-[16px] py-[12px] font-segoe text-[14px] text-[color:var(--black-text,#0d0d0d)]">
          {t('list.pending')}
        </p>
      )}

      {isLoading ? (
        <AccessibleLoader label={t('loading', { ns: 'common' })} />
      ) : audits.length === 0 ? (
        <p className="rounded-[16px] bg-[var(--grey-background,#f8f8f8)] px-[24px] py-[32px] text-center font-segoe text-[15px] text-[color:var(--grey-text,#565858)]">
          {t('list.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-[12px]">
          {audits.map((audit) => (
            <li
              key={audit.id}
              className="flex flex-wrap items-center gap-[12px] rounded-[16px] border border-[var(--grey-accent,#eeeeee)] px-[20px] py-[16px]"
            >
              <div className="flex min-w-[200px] flex-1 flex-col gap-[2px]">
                <span className="truncate font-manrope text-[15px] font-semibold text-[color:var(--black-text,#0d0d0d)]">
                  {audit.title || audit.url.replace(/^https?:\/\//, '')}
                </span>
                <span className="truncate font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
                  {audit.url.replace(/^https?:\/\//, '')}
                </span>
              </div>

              {audit.status === 'completed' && typeof audit.score === 'number' ? (
                <div className="flex items-baseline gap-[8px]">
                  <span className={cn('font-manrope text-[22px] font-bold', scoreTone(audit.score))}>
                    {audit.score}
                  </span>
                  <span className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
                    {t('list.issues', { count: audit.issues_count ?? 0 })}
                  </span>
                </div>
              ) : (
                <span className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
                  {t(`status.${audit.status}`)}
                </span>
              )}

              <div className="ml-auto flex gap-[8px]">
                {audit.status === 'completed' && (
                  <Button
                    variant="brand"
                    size="brand-sm"
                    onClick={() => navigate(`/site-audit/${audit.id}`)}
                  >
                    {t('list.open')}
                  </Button>
                )}
                <Button variant="brand-danger" size="brand-sm" onClick={() => setDeleteTarget(audit)}>
                  {t('list.delete')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title={t('list.deleteConfirm.title')}
        description={t('list.deleteConfirm.text')}
        confirmText={t('list.deleteConfirm.confirm')}
        cancelText={t('list.deleteConfirm.cancel')}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
